<template>
  <NuxtLayout name="standard">
    <ConnectifTag :name="CONNECTIF_PAGE_TAGS.OTHERS" />
    <ConnectifTag :name="CONNECTIF_CONTENT_TAGS.OTHERS" />

    <template v-if="village">
      <SSection :has-separator="false" :full-width="false" :centered="true">
        <div class="flex flex-col items-center lg:mt-4 lg:mb-14">
          <TTitle tag="h1" size="5xl" classes="text-center">
            {{
              t('landings.villages.title', {
                name: village.attributes.name,
              })
            }}
          </TTitle>
          <TText size="lg" classes="mt-5 text-center">
            {{
              t('landings.villages.description', {
                name: village.attributes.name,
              })
            }}
          </TText>
          <UButton
            color="neutral"
            variant="solid"
            class="mt-8"
            size="xl"
            :label="
              t('landings.villages.cottages', {
                name: village.attributes.name,
              })
            "
            :to="routes().cottageProvince({ slug: village.attributes.slug })"
            external
          />
        </div>
      </SSection>

      <SSectionColor
        v-if="dataPosts"
        :title="
          t('landings.villages.posts.title', {
            name: village.attributes.name,
          })
        "
        background-color="gray-50"
      >
        <SBlogposts v-bind="dataPosts" />
      </SSectionColor>

      <SSection
        v-if="villagesData && villagesData.villages.length > 0"
        :has-separator="false"
        :full-width="false"
        :centered="true"
        :title="t('landings.villages.links.title')"
      >
        <TText size="lg" class="text-center">
          {{
            t('landings.villages.links.description', {
              number: villagesData.villages.length,
              name: village.attributes.name,
            })
          }}
        </TText>
        <article
          v-for="(item, index) in villagesData.villages"
          :key="index"
          class="py-6"
        >
          <TText size="lg" tag="h3" variant="semibold" classes="mb-1">
            {{ item.name }}
          </TText>

          <TText classes="mb-2 text-ellipsis line-clamp-7 md:line-clamp-3">
            {{ item.description }}
          </TText>
          <UButton
            variant="link"
            size="sm"
            :label="t('landings.villages.links.link')"
            :to="routes().cottageProvince({ slug: item.slug })"
            external
            icon="i-heroicons-arrow-top-right-on-square-16-solid"
            trailing
          ></UButton>
        </article>
      </SSection>

      <SSpacer />
    </template>
  </NuxtLayout>
</template>

<script setup lang="ts">
// Constants
import {
  CONNECTIF_PAGE_TAGS,
  CONNECTIF_CONTENT_TAGS,
} from '~~/modules/integrations/connectif/constants'
import { sendConnectifPage } from '~~/modules/integrations/connectif/composables/useConnectifComposable'

// Types
import type { schemaType as Village } from '~~/modules/api/strapi/pueblos-landings/detail/schema'
import type { schemaType as VillagesData } from '~~/modules/api/rurality/v3/provinces/villages/schema'
import type { propsInterface as SBlogPostsPropsInterface } from '~~/modules/design-system/sections/s-blogposts.vue'

// Composables
import { useMenuBreadcrumbStore } from '~~/modules/menu/stores/useMenuBreadcrumbStore'
import { useHeadInfoComposable } from '~~/modules/head/composables/useHeadInfoComposable'

// Apis
import { serverFetch as getVillagesDetail } from '~~/modules/api/strapi/pueblos-landings/detail'
import { serverFetch as getVillages } from '~~/modules/api/rurality/v3/provinces/villages'
import { serverFetch as getBlogPosts } from '~~/modules/api/rurality/v3/blog/posts/list'

// Transformers
import { useToSBlogposts } from '~/composables/transformers/useToSBlogposts'

const route = useRoute()

const { setHeadInfo } = useHeadInfoComposable()

const showErrorPage = (error: ApiResponseError) => {
  throw createError({
    statusCode: 503,
    message: error?.message,
    fatal: true,
  })
}

const showErrorNotFound = () => {
  throw createError({
    statusCode: 404,
    fatal: true,
  })
}

const village = ref<Village | undefined>(undefined)
const dataPosts = ref<SBlogPostsPropsInterface | undefined>(undefined)
const villagesData = ref<VillagesData | undefined>(undefined)

const dataApiDetail = await getVillagesDetail({
  slug: route.params.slug as string,
})

if (
  dataApiDetail.success &&
  dataApiDetail.success.data.attributes.publishedAt
) {
  village.value = dataApiDetail.success.data as Village
  const getVillagesData = await getVillages({
    slug: village.value.attributes.slug,
  })
  if (getVillagesData.success && getVillagesData.success.data) {
    villagesData.value = getVillagesData.success.data
  }
  if (village.value.attributes.blog_posts) {
    const getPosts = await getBlogPosts({
      slugs: village.value.attributes.blog_posts,
    })
    if (getPosts.success && getPosts.success.data) {
      dataPosts.value = useToSBlogposts().fromBlogPostList(
        getPosts.success.data,
      )
    }
  }
} else if (
  dataApiDetail.success &&
  !dataApiDetail.success.data.attributes.publishedAt
) {
  showErrorNotFound()
} else if (dataApiDetail.error) {
  showErrorPage(dataApiDetail.error as ApiResponseError)
}

if (village.value) {
  setHeadInfo({
    title: t('landings.villages.seo.title', {
      name: village.value.attributes.name,
    }),
    description: t('landings.villages.seo.description', {
      name: village.value.attributes.name,
    }),
    robotsVisible: true,
    images: [
      {
        url: dataPosts.value ? dataPosts.value.posts[0].media.img : '',
        alt: t('landings.villages.seo.title', {
          name: village.value.attributes.name,
        }),
      },
    ],
  })

  const breadCrumbMenuStore = useMenuBreadcrumbStore()
  breadCrumbMenuStore.setMenuBreadcrumb(village.value.attributes.name, [
    {
      href: routes().villagesList(),
      name: t('menu.breadcrumb.villages'),
    },
  ])
}

sendConnectifPage()
</script>
